import { useContext, useEffect, useMemo, useState } from 'react';

import useError from './useError';
import * as api from '../lib/api/api';
import { ERROR_CS, STATUS_MESSAGE } from '../lib/Error';
import AffiliateContext from '../modules/contexts/AffiliateContext';

const useVoucher = ({ status }) => {
  const { onHandleStatusError } = useError();

  const [ isLoading, setIsLoading ] = useState(false);
  const [ isVoucherCouponLoaded, setIsVoucherCouponLoaded ] = useState(false);
  const [ isRegisterLoading, setIsRegisterLoading ] = useState(false);
  const [ voucherCouponList, setVoucherCouponList ] = useState([]);
  const [ selectedVoucherCouponList, setSelectedVoucherCouponList ] = useState([]);
  const [ voucherCouponError, setVoucherCouponError ] = useState('');

  const { state: { affiliateCouponCode } } = useContext(AffiliateContext);

  useEffect(() => {
    fetchVoucherCouponList();
  }, []);

  // 쿠폰 및 바우처 조회
  const fetchVoucherCouponList = () => {
    setIsLoading(true);
    setIsVoucherCouponLoaded(false);
    api.getVoucherCouponList({ status })
      .then((response) => {
        setIsVoucherCouponLoaded(true);
        if (status === 'usable') {
          setVoucherCouponList(response.data.filter(el => el.status === 'DELEGATED' && el.expirationDate ? calculateDday(el.expirationDate) >= 0 : true))
        } else {
          setVoucherCouponList(response.data)
        }
        setVoucherCouponError('');
      }).finally(() => {
        setIsLoading(false);
      });
  };

  // 쿠폰 및 바우처 등록
  const registerVoucherCoupon = async (code) => {
    const message = STATUS_MESSAGE.voucher;

    // 사전 예외처리 - 입력이 안되었거나 이미 등록된 코드인 경우
    if (code === '') return;
    if (voucherCouponList.find(el => el.code === code)) {
      setVoucherCouponError(message[409]);
      return;
    }

    setIsRegisterLoading(true);
    try {
      const response = await api.postVoucherCoupon({ code });
      fetchVoucherCouponList();
      return response.data;
    } catch (error) {
      onHandleStatusError({
        message,
        error,
        errorHandler: () =>
          setVoucherCouponError(message[error.response.status]),
      });
    } finally {
      setIsRegisterLoading(false);
    }
  };

  // 쿠폰 및 바우처 등록 (UI 없음)
  const registerVoucherCouponBackground = async (code) => {
    // 사전 예외처리 - 입력이 안되었거나 이미 등록된 코드인 경우
    if (code === '' || voucherCouponList.find(el => el.code === code)) return;
    try {
      const response = await api.postVoucherCoupon({ code });
      fetchVoucherCouponList();
      return response.data;
    } catch (error) {
      return;
    }
  };

  // 쿠폰 및 바우처 선택/선택 해제시 테스트할 쿠폰 리스트 반환
  const getVoucherCouponSelectionList = (id) => {
    const selectedCoupon = voucherCouponList.find((coupon) => coupon.id === id);
    
    // 쿠폰이 없는 경우 return
    if (!selectedCoupon) return;

    // 상품권 선택시 제휴사 쿠폰은 비활성화 처리
    if (affiliateCouponId && selectedCoupon.voucherType !== 'COUPON') {
      return selectedVoucherCouponList.includes(id) ?
        [...selectedVoucherCouponList.filter((el) => el !== id), affiliateCouponId] :
        [...selectedVoucherCouponList.filter((el) => el !== affiliateCouponId), id];
    } else {
      return selectedVoucherCouponList.includes(id) ? 
        selectedVoucherCouponList.filter((el) => el !== id) : 
        [...selectedVoucherCouponList, id];
    }
  };

  const setVoucherCouponSelectionList = (list) => {
    if (!list) return;
    setSelectedVoucherCouponList(list);
  }

  // D-Day 계산
  const calculateDday = (expirationDate) => {
    if (!expirationDate) return 9999;
    const dayDistance =
      Date.parse(expirationDate.replace(' ', 'T')) - new Date();
    const dDay = Math.floor(dayDistance / (1000 * 60 * 60 * 24));
    return dDay;
  };

  // 날짜 포맷 변경
  const replaceDateFormat = (expirationDate) => {
    return expirationDate ? expirationDate.replace(/-/gi, '.').split(' ', 1) : "";
  };

  // 사용 가능한 쿠폰 갯수
  const numberOfUsableVoucherCoupon = voucherCouponList.filter(
    (el) => el.status === 'DELEGATED' && el.expirationDate ? calculateDday(el.expirationDate) > 0 : true,
  ).length;

  const affiliateCouponId = useMemo(() => {
    if (affiliateCouponCode === null) return null;
    const affiliateCoupon = voucherCouponList.find(el => el.code === affiliateCouponCode) || registerVoucherCouponBackground(affiliateCouponCode);
    return affiliateCoupon ? affiliateCoupon.id : null;
  }, [affiliateCouponCode, voucherCouponList]);

  return {
    isRegisterLoading,
    isLoading,
    isVoucherCouponLoaded,
    numberOfUsableVoucherCoupon,
    voucherCouponList,
    selectedVoucherCouponList,
    voucherCouponError,
    affiliateCouponId,
    fetchVoucherCouponList,
    registerVoucherCoupon,
    registerVoucherCouponBackground,
    getVoucherCouponSelectionList,
    setVoucherCouponSelectionList,
    setIsLoading,
    calculateDday,
    replaceDateFormat,
  };
};

export default useVoucher;
